import { useState, useMemo } from "react";
import {
  Stack,
  Typography,
  IconButton,
  Box,
  Tooltip,
  Switch,
  Pagination,
  TablePagination,
} from "@mui/material";
import SwitchAccountOutlinedIcon from "@mui/icons-material/SwitchAccountOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {
  DataGridPro,
  GridColDef,
  GridRowSelectionModel,
} from "@mui/x-data-grid-pro";
import DataStatusAlert from "../UI_HelpersOf_DataAccuracy/DataStatusAlert_Helpers";

import SettingsDataAccuracy from "../SettingsDataAccuracy/SettingsDataAccuracy";
import ResolveDataAccuracy from "../ResolveDataAccuracy/ResolveDataAccuracy";
import AssignDataAccuracy from "../AssignDataAccuracy/AssignDataAccuracy";
import { useSelector } from "react-redux";
import { formatDateToDDMMYY, getSiteInfo } from "../../../../../utils/helpers";
import { setSelectedRowsData } from "../../../../../features/dataAccuracySlice";
import { useDispatch } from "react-redux";
import TaskNameDAFilter from "./TaskNameDAFilter";
import { selectUserWebsitePermissions } from "../../../../../features/userSlice";
import { useSearchParams } from "react-router-dom";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import StatusLogDialog from "./StatusLogDialog";
import TaskDetailPanel from "./TaskDetailPanel";
import { exportDataAccuracyTasksToCSV } from "../DataAccuracyHelpers/helpers";

const GroupedOccurrencesTable = () => {
  const [snoozeDialogOpen, setSnoozeDialogOpen] = useState(false);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [statusLogDialogOpen, setStatusLogDialogOpen] = useState(false);
  const [selectedStatusLog, setSelectedStatusLog] = useState<any[]>([]);
  const handleOpenSnooze = () => setSnoozeDialogOpen(true);
  const handleCloseSnooze = () => setSnoozeDialogOpen(false);
  const handleOpenResolve = () => setResolveDialogOpen(true);
  const handleCloseResolve = () => setResolveDialogOpen(false);
  const handleOpenAssign = () => setAssignDialogOpen(true);
  const handleCloseAssign = () => setAssignDialogOpen(false);

  const [hideDone, setHideDone] = useState(true);
  const allWebsites = useSelector(selectUserWebsitePermissions);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [selectedRowIds, setSelectedRowIds] = useState<GridRowSelectionModel>();
  const [selectedDetailTask, setSelectedDetailTask] = useState<any>(null);
  const [searchParams] = useSearchParams();

  // Parse Assignee from &filters=Assignee%3DNAME1%2CNAME2
  // After one decode: "Assignee=MRITYUNJOY%20MANDAL,Ortal%20Adiv"
  // Multiple assignees are comma-separated within the Assignee= value.
  const assigneeFromUrl = useMemo(() => {
    const filtersParam = searchParams.get("filters");
    if (!filtersParam) return null;

    // Decode once: %3D → =, %2C → ,
    const decoded = decodeURIComponent(filtersParam);

    // Match everything after "Assignee=" until the next "&" (other filter keys)
    const match = decoded.match(/(?:^|&)Assignee=([^&]+)/i);
    if (!match) return null;

    // Split by comma, decode each name (%20 → space), normalise to lowercase
    const names = match[1]
      .split(",")
      .map((name) => decodeURIComponent(name).trim().toLowerCase())
      .filter(Boolean);

    return names.length > 0 ? names : null;
  }, [searchParams]);

  const {
    tasksByTaskStructureSharedUid,
    selectedRowsData,
    selectedAsset,
    selectedStatus,
    selectedLastUpdatedDate,
    selectedColumnFilters,
  } = useSelector((state: any) => state.dataAccuracy);

  const dispatch = useDispatch();

  // Extract non-mutable data, mutable data (metrics) and create dynamic columns
  const { dataColumns, processedRows, dataFieldsMeta, occurrenceFieldsMeta } =
    useMemo(() => {
      if (
        !tasksByTaskStructureSharedUid ||
        !Array.isArray(tasksByTaskStructureSharedUid)
      ) {
        return {
          dataColumns: [],
          processedRows: [],
          dataFieldsMeta: [],
          occurrenceFieldsMeta: [],
        };
      }

      // Extract fields from data (excluding log_data) - these are non-output fields
      const dataFields = new Map();
      // Extract fields from log_data - these are occurrence/output fields
      const occurrenceFields = new Map();

      tasksByTaskStructureSharedUid.forEach((task) => {
        if (task.data) {
          // Process data fields (excluding log_data)
          const dataWithoutLogData = { ...task.data };
          delete dataWithoutLogData.log_data;

          Object.entries(dataWithoutLogData).forEach(
            ([key, fieldData]: [string, any]) => {
              if (
                fieldData &&
                typeof fieldData === "object" &&
                fieldData.hasOwnProperty("order") &&
                fieldData.order !== null &&
                fieldData.order !== undefined &&
                Number(fieldData.order) > 0 &&
                key.startsWith("query_output:")
              ) {
                if (!dataFields.has(key)) {
                  dataFields.set(key, {
                    field: key,
                    headerName: fieldData.title,
                    order: Number(fieldData.order),
                    description: fieldData.description || "",
                    type: fieldData.type || "string",
                  });
                }
              }
            },
          );

          // Process occurrence fields from log_data (order > 0, output fields)
          if (task.data.log_data && Array.isArray(task.data.log_data)) {
            task.data.log_data.forEach((logEntry: any) => {
              Object.entries(logEntry).forEach(
                ([logKey, logData]: [string, any]) => {
                  if (
                    logData &&
                    typeof logData === "object" &&
                    logData.hasOwnProperty("order") &&
                    logData.order !== null &&
                    logData.order !== undefined &&
                    Number(logData.order) > 0 &&
                    logKey.startsWith("query_output:")
                  ) {
                    if (!occurrenceFields.has(logKey)) {
                      occurrenceFields.set(logKey, {
                        field: logKey,
                        headerName:
                          logData.title ||
                          logKey
                            .replace(/^query_output:/, "")
                            .replace(/_/g, " ")
                            .replace(/^\w/, (c) => c.toUpperCase()),
                        order: Number(logData.order),
                        description: logData.description || "",
                        type: logData.type || "string",
                      });
                    }
                  }
                },
              );
            });
          }
        }
      });

      // Sort columns by order
      const sortedDataFields = Array.from(dataFields.values()).sort(
        (a, b) => a.order - b.order,
      );
      const sortedOccurrenceFields = Array.from(occurrenceFields.values()).sort(
        (a, b) => a.order - b.order,
      );

      // The row's "identity" (shown in Master View and as the Detail View
      // title) is a Brand Name + Brand ID pair when both exist, rather than
      // whichever field happens to have the lowest `order` — that varies by
      // task type and can be a date or a bare numeric field with no meaning
      // on its own.
      const brandNameField = sortedDataFields.find(
        (f) =>
          /brand/i.test(f.headerName) && /(name|display)/i.test(f.headerName),
      );
      const brandIdField = sortedDataFields.find(
        (f) => /brand/i.test(f.headerName) && /\bid\b/i.test(f.headerName),
      );
      const IDENTITY_FIELD = "__brand_identity__";

      const effectiveDataFields = brandNameField
        ? [
            { ...brandNameField, field: IDENTITY_FIELD },
            ...sortedDataFields.filter(
              (f) => f !== brandNameField && f !== brandIdField,
            ),
          ]
        : sortedDataFields;

      // Create data columns (from main data object, excluding log_data)
      const dataCols: GridColDef[] = effectiveDataFields.map((field) => ({
        field: field.field,
        headerName: field.headerName,
        flex: 1,
        renderCell: (params: any) => {
          const value = params.value;
          return (
            <Tooltip title={value || "-"} arrow>
              {value || " "}
            </Tooltip>
          );
        },
      }));

      // No grouping — each task becomes its own row
      const rows = tasksByTaskStructureSharedUid.map(
        (task: any, index: number) => {
          // Last occurrence date from this task's last log_data entry
          let lastOccurrenceDate = " ";
          if (task.data?.log_data && Array.isArray(task.data.log_data)) {
            const lastLogEntry =
              task.data.log_data[task.data.log_data.length - 1];
            if (lastLogEntry && lastLogEntry["meta_data:start_run"]) {
              const startRunValue = lastLogEntry["meta_data:start_run"].value;
              if (startRunValue) lastOccurrenceDate = startRunValue;
            }
          }

          const row: any = {
            id: index + 1,
            allTasks: [task],
            originalTask: task,
            created: task.created_at || null,
            updated: task.modified_at || null,
            assignee: task.assignee_fullname || " ",
            status: task.current_status || "",
            occurrences_count: 1,
            site_name: getSiteInfo(allWebsites, task.website_shared_uid) || " ",
            website_shared_uid: task.website_shared_uid,
            occurrence_date: lastOccurrenceDate,
          };

          // Add data field values (from main data object, excluding log_data)
          if (task.data) {
            const dataWithoutLogData = { ...task.data };
            delete dataWithoutLogData.log_data;

            sortedDataFields.forEach((field) => {
              const fieldData = dataWithoutLogData[field.field];
              if (fieldData && fieldData.hasOwnProperty("value")) {
                row[field.field] = fieldData.value;
              }
            });

            if (brandNameField) {
              const nameValue = row[brandNameField.field];
              const idValue = brandIdField
                ? row[brandIdField.field]
                : undefined;
              row[IDENTITY_FIELD] =
                idValue !== undefined && idValue !== null && idValue !== ""
                  ? `${nameValue} (${idValue})`
                  : nameValue;
            }

            // Add occurrence field values from the last log_data entry
            if (task.data.log_data && Array.isArray(task.data.log_data)) {
              const lastLogEntry =
                task.data.log_data[task.data.log_data.length - 1];
              Object.entries(lastLogEntry).forEach(
                ([logKey, logData]: [string, any]) => {
                  if (
                    logData &&
                    typeof logData === "object" &&
                    logData.hasOwnProperty("value")
                  ) {
                    row[logKey] = logData.value;
                  }
                },
              );
            }
          }

          return row;
        },
      );

      return {
        dataColumns: dataCols,
        processedRows: rows,
        dataFieldsMeta: effectiveDataFields.map((field) => ({
          field: field.field,
          headerName: field.headerName,
          type: field.type,
        })),
        occurrenceFieldsMeta: sortedOccurrenceFields.map((field) => ({
          field: field.field,
          headerName: field.headerName,
          type: field.type,
        })),
      };
    }, [tasksByTaskStructureSharedUid, allWebsites]);

  // Define all columns in the new order
  const columns: GridColDef[] = useMemo(() => {
    // 1. Asset Column
    const assetColumn: GridColDef = {
      field: "site_name",
      headerName: "Asset",
      flex: 1,
      minWidth: 140,
      renderCell: (params: any) => (
        <Tooltip title={params.value || "-"} arrow>
          {params.value || "-"}
        </Tooltip>
      ),
    };

    // 2. First data column only (first item by order from data → query_output).
    // The remaining data columns and the log_data occurrence columns are no
    // longer shown in the Master View — they now live in the Detail View
    // (see TaskDetailPanel: Task Data / Task Info sections).
    const firstDataColumn = dataColumns.length > 0 ? [dataColumns[0]] : [];

    // 3. Last Occurrence Column
    const occurrenceDateColumn: GridColDef = {
      field: "occurrence_date",
      headerName: "Last Occurrence",
      flex: 0.8,
      minWidth: 120,
      renderCell: (params: any) => {
        const formattedDate = params.value
          ? formatDateToDDMMYY(params.value)
          : ("" as any);
        return (
          <Tooltip title={formattedDate} arrow>
            {formattedDate}
          </Tooltip>
        );
      },
    };

    // 4. Operational columns (Status, Created)
    const operationalColumns: GridColDef[] = [
      {
        field: "status",
        headerName: "Status",
        flex: 0.2,
        minWidth: 120,
        renderCell: (params: any) => (
          <Stack
            sx={{ height: "100%" }}
            direction="row"
            alignItems="center"
            justifyContent="center"
            width="100px"
          >
            {params.value === "To Do" && <DataStatusAlert status="To Do" />}
            {params.value === "Snoozed" && <DataStatusAlert status="Snoozed" />}
            {params.value === "Snooze" && <DataStatusAlert status="Snoozed" />}
            {params.value === "In Progress" && (
              <DataStatusAlert status="In Progress" />
            )}
            {params.value === "Done" && <DataStatusAlert status="Done" />}
          </Stack>
        ),
      },
      {
        field: "created",
        headerName: "Created",
        flex: 0.2,
        minWidth: 90,
        resizable: true,
        renderCell: (params: any) =>
          params.value ? formatDateToDDMMYY(params.value) : " ",
      },
    ];

    // Combine in the new order
    return [
      assetColumn,
      ...firstDataColumn,
      occurrenceDateColumn,
      ...operationalColumns,
    ];
  }, [dataColumns]);

  // Fields that already have a dedicated static filter in TaskNameDAFilter
  // (Asset, Status, Last Updated) — kept as a defensive skip-list in case a
  // dynamic field name ever collides with one of these.
  const ALREADY_FILTERED_FIELDS = useMemo(
    () => new Set(["site_name", "status", "updated", "occurrences_count"]),
    [],
  );

  // A field counts as a date column when its declared query-output type
  // mentions "date"/"timestamp" — this drives whether it gets a date-range
  // picker instead of a checkbox list.
  const isDateFieldType = (type: unknown) =>
    typeof type === "string" && /date|timestamp/i.test(type);

  // Only the field actually shown as a Master View column (the first item by
  // order — see dataFieldsMeta) gets a dynamic filter. The remaining data
  // fields and occurrence fields now live only in the Detail View (Task
  // Data / Task Info) and are intentionally excluded here.
  const dynamicFilterableColumns = useMemo(() => {
    const firstField = dataFieldsMeta[0];
    const fromDataColumns =
      firstField && !ALREADY_FILTERED_FIELDS.has(firstField.field)
        ? [
            {
              field: firstField.field,
              headerName: firstField.headerName || firstField.field,
              isDateRangeFilter: isDateFieldType(firstField.type),
            },
          ]
        : [];

    return [
      ...fromDataColumns,
      {
        field: "occurrence_date",
        headerName: "Last Occurrence",
        isDateRangeFilter: true,
      },
    ];
  }, [dataFieldsMeta, ALREADY_FILTERED_FIELDS]);

  // Field names that use a date-range comparison rather than exact-match
  // checkboxes, used by filteredRows below.
  const dateFilterFieldSet = useMemo(
    () =>
      new Set(
        dynamicFilterableColumns
          .filter((col) => col.isDateRangeFilter)
          .map((col) => col.field),
      ),
    [dynamicFilterableColumns],
  );

  const filteredRows = useMemo(() => {
    let rows = processedRows;
    if (hideDone) {
      rows = rows.filter(
        (row: any) => !["Done", "Snooze", "Snoozed"].includes(row.status),
      );
    }

    if (selectedAsset && selectedAsset.length > 0) {
      rows = rows.filter((row: any) => selectedAsset.includes(row.site_name));
    }

    if (selectedStatus && selectedStatus.length > 0) {
      rows = rows.filter((row: any) => selectedStatus.includes(row.status));
    }

    if (selectedLastUpdatedDate && selectedLastUpdatedDate.length === 2) {
      const [startDate, endDate] = selectedLastUpdatedDate;

      rows = rows.filter((row: any) => {
        const parsedDate = new Date(row.created);
        const isoDate = parsedDate.toISOString().split("T")[0];
        return isoDate >= startDate && isoDate <= endDate;
      });
    }

    // Filter by Assignee URL param (e.g. &filters=Assignee%3DNAME1%2CNAME2)
    if (assigneeFromUrl) {
      rows = rows.filter(
        (row: any) =>
          typeof row.assignee === "string" &&
          assigneeFromUrl.includes(row.assignee.trim().toLowerCase()),
      );
    }

    // Apply dynamically generated per-column filters (skips columns that
    // already have a dedicated filter above).
    if (selectedColumnFilters) {
      Object.entries(selectedColumnFilters).forEach(
        ([field, values]: [string, any]) => {
          if (!Array.isArray(values) || values.length === 0) return;

          if (dateFilterFieldSet.has(field)) {
            // Date-range filter — same "parse raw value, compare ISO date"
            // logic as the existing Last Updated filter, so it matches
            // whatever raw date format the row actually holds.
            if (values.length === 2 && values[0] && values[1]) {
              const [startDate, endDate] = values;
              rows = rows.filter((row: any) => {
                const parsedDate = new Date(row[field]);
                if (isNaN(parsedDate.getTime())) return false;
                const isoDate = parsedDate.toISOString().split("T")[0];
                return isoDate >= startDate && isoDate <= endDate;
              });
            }
          } else {
            rows = rows.filter((row: any) => {
              const cellValue =
                row[field] === undefined || row[field] === null
                  ? ""
                  : String(row[field]);
              return values.includes(cellValue);
            });
          }
        },
      );
    }

    return rows;
  }, [
    hideDone,
    processedRows,
    selectedAsset,
    selectedStatus,
    selectedLastUpdatedDate,
    selectedColumnFilters,
    dateFilterFieldSet,
    assigneeFromUrl,
  ]);

  const handleRowSelectionChange = (newSelection: any) => {
    setSelectedRowIds(newSelection);

    const normalizeId = (id: any) =>
      typeof id === "string" && /^\d+$/.test(id) ? Number(id) : id;

    const extractIdsFromIdsField = (idsField: any): any[] => {
      if (idsField == null) return [];

      if (idsField instanceof Set) return Array.from(idsField).map(normalizeId);

      if (Array.isArray(idsField)) return idsField.map(normalizeId);

      if (typeof idsField.values === "function") {
        try {
          return Array.from(idsField).map(normalizeId);
        } catch (e) {
          // fallthrough
        }
      }

      if (typeof idsField === "object") {
        return Object.keys(idsField)
          .filter((k) => !!(idsField as any)[k])
          .map((k) => normalizeId(k));
      }

      return [normalizeId(idsField)];
    };

    let selectedIds: any[] = [];
    if (Array.isArray(newSelection)) {
      selectedIds = newSelection.map(normalizeId);
    } else if (
      newSelection &&
      typeof newSelection === "object" &&
      "ids" in newSelection
    ) {
      selectedIds = extractIdsFromIdsField(newSelection.ids);
    } else {
      selectedIds = extractIdsFromIdsField(newSelection);
    }

    const idSet = new Set(selectedIds.map(String));
    const selectedRows = filteredRows.filter((row: any) =>
      idSet.has(String(row.id)),
    );

    const allSelectedTasks = selectedRows.flatMap(
      (row: any) => row.allTasks || [row.originalTask],
    );
    dispatch(setSelectedRowsData(allSelectedTasks));
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPaginationModel((prev) => ({ ...prev, page: newPage }));
    // The selected row may not be on the new page anymore, so close the
    // Detail View rather than leave it showing a row that's no longer visible.
    setSelectedDetailTask(null);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setPaginationModel({
      page: 0,
      pageSize: +event.target.value,
    });
    setSelectedDetailTask(null);
  };

  const handleOpenStatusLog = (statusLog: any[]) => {
    setSelectedStatusLog(statusLog || []);
    setStatusLogDialogOpen(true);
  };

  const handleCloseStatusLog = () => {
    setStatusLogDialogOpen(false);
    setSelectedStatusLog([]);
  };

  return (
    <>
      <Stack
        direction="row"
        gap={1}
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography variant="h5" component="h2">
          Tasks
        </Typography>
        <Stack direction="column" alignItems="flex-end" gap={1}>
          <Stack direction="row" alignItems="center" gap={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2">Actions:</Typography>
              <Tooltip title="Export to CSV">
                <IconButton
                  size="small"
                  color="inherit"
                  onClick={() =>
                    exportDataAccuracyTasksToCSV(selectedRowsData, allWebsites)
                  }
                  disabled={selectedRowsData.length === 0}
                  data-tracker="675"
                >
                  <FileDownloadOutlinedIcon
                    fontSize="medium"
                    color={
                      selectedRowsData.length === 0 ? "disabled" : "secondary"
                    }
                  />
                </IconButton>
              </Tooltip>
              <Tooltip title="Settings">
                <IconButton
                  size="small"
                  color="inherit"
                  onClick={handleOpenSnooze}
                  disabled={selectedRowsData.length === 0}
                  data-tracker="603"
                >
                  <TuneOutlinedIcon
                    fontSize="medium"
                    color={
                      selectedRowsData.length === 0 ? "disabled" : "secondary"
                    }
                  />
                </IconButton>
              </Tooltip>
              <Tooltip title="Assign">
                <IconButton
                  size="small"
                  color="inherit"
                  onClick={handleOpenAssign}
                  disabled={selectedRowsData.length === 0}
                  data-tracker="169"
                >
                  <SwitchAccountOutlinedIcon
                    fontSize="medium"
                    color={
                      selectedRowsData.length === 0 ? "disabled" : "secondary"
                    }
                  />
                </IconButton>
              </Tooltip>
              <Tooltip title="Resolve" onClick={handleOpenResolve}>
                <IconButton
                  data-tracker="167"
                  size="small"
                  color="inherit"
                  disabled={selectedRowsData.length === 0}
                >
                  <CheckCircleOutlineIcon
                    fontSize="medium"
                    color={
                      selectedRowsData.length === 0 ? "disabled" : "secondary"
                    }
                  />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
          <Stack
            direction="row-reverse"
            alignItems="center"
            justifyContent="space-between"
            spacing={9}
          >
            <Switch
              size="small"
              checked={hideDone}
              onChange={(e) => setHideDone(e.target.checked)}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: "#9C27B0",
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "#9C27B0",
                },
              }}
            />
            <Typography variant="body2">Hide Done</Typography>
          </Stack>
        </Stack>
      </Stack>

      <Stack mt={1} data-tracker="166">
        <TaskNameDAFilter
          dynamicColumns={dynamicFilterableColumns}
          rows={processedRows}
        />
      </Stack>

      <Stack
        direction="row"
        sx={{
          width: "100%",
          marginTop: 2,
          border: "1px solid #e0e0e0",
          // borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            flex: "1 1 50%",
            minWidth: 0,
            display: "grid",
            "& .MuiDataGrid-root": {
              height: "100% !important",
            },
          }}
        >
          <DataGridPro
            sx={{
              "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
                outline: "none",
              },
              "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
                { outline: "none" },
              "& .MuiDataGrid-cellEmpty": {
                width: "auto",
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
              "& .MuiDataGrid-row.Mui-selected": {
                backgroundColor: "rgba(156, 39, 176, 0.08) !important",
                "&:hover": {
                  backgroundColor: "rgba(156, 39, 176, 0.16) !important",
                },
              },
              "& .MuiDataGrid-row.detail-selected-row": {
                backgroundColor: "rgba(156, 39, 176, 0.08) !important",
              },
              "& .MuiDataGrid-cell.Mui-selected": {
                backgroundColor: "inherit !important",
              },
              "& .MuiCheckbox-root": {
                color: "#9c27b0 !important",
                padding: "4px",
              },
              "& .MuiCheckbox-root.Mui-checked": {
                color: "#9c27b0",
              },
              "& .MuiCheckbox-root svg": {
                fontSize: "18px",
              },
              border: "none",
              "& .MuiDataGrid-main": {
                overflow: "visible",
              },
              "& .MuiDataGrid-columnHeaders": {
                position: "sticky",
                top: 0,
                zIndex: 1,
                backgroundColor: "white",
                borderBottom: "1px solid #e0e0e0",
              },
              "& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell": {
                borderRight: "1px solid #eeeeee",
              },
              "& .MuiDataGrid-columnHeader:last-child, & .MuiDataGrid-cell:last-child":
                {
                  borderRight: "none",
                },
              "& .MuiDataGrid-row": {
                cursor: "pointer",
                borderBottom: "1px solid #eeeeee",
              },
              "& .MuiDataGrid-footerContainer": {
                display: "none",
              },
            }}
            density="compact"
            pagination={true}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50, 100]}
            rows={filteredRows}
            columns={columns}
            disableColumnMenu
            disableColumnFilter
            disableColumnReorder
            sortingOrder={["asc", "desc"]}
            isCellEditable={() => false}
            disableRowSelectionOnClick
            isRowSelectable={() => true}
            checkboxSelection
            rowSelectionModel={selectedRowIds}
            onRowSelectionModelChange={handleRowSelectionChange}
            onRowClick={(params) =>
              setSelectedDetailTask((prev: any) =>
                prev?.id === params.row.id ? null : params.row,
              )
            }
            getRowClassName={(params) =>
              selectedDetailTask?.id === params.id ? "detail-selected-row" : ""
            }
            // autoHeight={false}
            style={{ height: "100%" }}
          />
        </Box>

        <TaskDetailPanel
          task={selectedDetailTask}
          dataFieldsMeta={dataFieldsMeta}
          occurrenceFieldsMeta={occurrenceFieldsMeta}
          onOpenStatusLog={handleOpenStatusLog}
        />
      </Stack>

      {/* Custom Pagination */}
      <Stack
        spacing={1}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mt: 2 }}
      >
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredRows?.length}
          rowsPerPage={paginationModel.pageSize}
          page={paginationModel.page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            ".MuiTablePagination-actions": {
              display: "none",
            },
            ".MuiTablePagination-toolbar": {
              paddingLeft: "16px",
            },
          }}
        />
        <Pagination
          count={Math.ceil(filteredRows?.length / paginationModel.pageSize)}
          showFirstButton
          showLastButton
          color="secondary"
          size="small"
          onChange={(_, value) => handleChangePage(null, value - 1)}
          page={paginationModel.page + 1}
        />
        <Box sx={{ width: "220px" }} />
      </Stack>

      <SettingsDataAccuracy
        open={snoozeDialogOpen}
        onClose={handleCloseSnooze}
      />
      <ResolveDataAccuracy
        open={resolveDialogOpen}
        onClose={handleCloseResolve}
      />
      <AssignDataAccuracy open={assignDialogOpen} onClose={handleCloseAssign} />
      <StatusLogDialog
        open={statusLogDialogOpen}
        onClose={handleCloseStatusLog}
        statusLog={selectedStatusLog}
      />
    </>
  );
};

export default GroupedOccurrencesTable;
